import { Download, FilePlus2, LoaderCircle, Paperclip, RefreshCw, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { formatDate } from '../lib/project'
import type { AttachmentMetadata, ProjectAttachment } from '../types'

type AttachmentDrawerProps = {
  projectName: string
  stepId?: string
  stepName?: string
  attachments: ProjectAttachment[]
  busy: boolean
  onClose: () => void
  onUpload: (file: File, replaces?: ProjectAttachment) => Promise<void>
  onUpdate: (attachment: ProjectAttachment, metadata: AttachmentMetadata) => Promise<void>
  onDownload: (attachment: ProjectAttachment) => Promise<void>
}

function metadataOf(attachment: ProjectAttachment): AttachmentMetadata {
  return {
    title: attachment.title ?? '',
    category: attachment.category ?? '',
    document_date: attachment.document_date ?? '',
    received_date: attachment.received_date ?? '',
    issuer: attachment.issuer ?? '',
    note: attachment.note ?? '',
  }
}

function fileSize(bytes: number) {
  if (bytes < 1_024) return `${bytes} o`
  if (bytes < 1_048_576) return `${Math.round(bytes / 1_024)} Ko`
  return `${(bytes / 1_048_576).toFixed(1).replace('.', ',')} Mo`
}

export function AttachmentDrawer({
  projectName,
  stepId,
  stepName,
  attachments,
  busy,
  onClose,
  onUpload,
  onUpdate,
  onDownload,
}: AttachmentDrawerProps) {
  const [drafts, setDrafts] = useState<Record<string, AttachmentMetadata>>({})
  const [workingId, setWorkingId] = useState<string | null>(null)
  const [mobileReadOnly, setMobileReadOnly] = useState(false)
  const replacedIds = new Set(attachments.flatMap((attachment) => attachment.replaces_attachment_id ? [attachment.replaces_attachment_id] : []))

  useEffect(() => {
    setDrafts(Object.fromEntries(attachments.map((attachment) => [attachment.id, metadataOf(attachment)])))
  }, [attachments])

  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)')
    const sync = () => setMobileReadOnly(media.matches)
    sync()
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])

  function setField(attachment: ProjectAttachment, field: keyof AttachmentMetadata, value: string) {
    setDrafts((current) => ({
      ...current,
      [attachment.id]: { ...(current[attachment.id] ?? metadataOf(attachment)), [field]: value },
    }))
  }

  async function saveMetadata(attachment: ProjectAttachment) {
    setWorkingId(attachment.id)
    await onUpdate(attachment, drafts[attachment.id] ?? metadataOf(attachment))
    setWorkingId(null)
  }

  async function upload(file: File, replaces?: ProjectAttachment) {
    setWorkingId(replaces?.id ?? 'new')
    await onUpload(file, replaces)
    setWorkingId(null)
  }

  return (
    <div className="drawer-backdrop attachment-backdrop" role="presentation" onMouseDown={onClose}>
      <aside className="attachment-drawer" role="dialog" aria-modal="true" aria-labelledby="attachment-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><span className="eyebrow">PIÈCES JOINTES FACULTATIVES</span><h2 id="attachment-title">{stepName ?? 'Documents du projet'}</h2><p>{projectName}{stepId ? ` · ${stepId}` : ''}</p></div>
          <button className="icon-button" aria-label="Fermer les pièces jointes" onClick={onClose} type="button"><X size={20} /></button>
        </header>

        <section className="attachment-upload-card">
          <Paperclip size={22} />
          <div><strong>{mobileReadOnly ? 'Consultation des fichiers' : 'Ajouter un fichier'}</strong><span>{mobileReadOnly ? 'Les modifications sont disponibles sur ordinateur.' : 'Vous pourrez le qualifier maintenant ou plus tard.'}</span></div>
          <label className={`primary-button attachment-upload-button${mobileReadOnly ? ' is-disabled' : ''}`}>
            {workingId === 'new' ? <LoaderCircle className="spin" size={17} /> : <FilePlus2 size={17} />} Choisir un fichier
            <input disabled={mobileReadOnly || busy || workingId !== null} type="file" onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file); event.currentTarget.value = '' }} />
          </label>
          <small>25 Mo maximum. Aucun type, titre ou date n’est obligatoire.</small>
        </section>

        <div className="attachment-list">
          {!attachments.length ? <div className="attachment-empty"><Paperclip size={24} /><strong>Aucun fichier joint</strong><span>Le suivi reste parfaitement utilisable sans pièce jointe.</span></div> : null}
          {attachments.map((attachment) => {
            const draft = drafts[attachment.id] ?? metadataOf(attachment)
            return (
              <article className="attachment-card" key={attachment.id}>
                <header>
                  <div className="attachment-file-icon"><Paperclip size={18} /></div>
                  <div><strong>{attachment.title || attachment.filename}</strong><span>{attachment.filename} · {fileSize(attachment.file_size)} · version {attachment.version_number}</span><small>Ajouté le {formatDate(attachment.created_at.slice(0, 10))}</small></div>
                  <button className="secondary-button compact" onClick={() => void onDownload(attachment)} disabled={busy} type="button"><Download size={16} /> Télécharger</button>
                </header>

                <details>
                  <summary>Qualifier ce fichier <span>facultatif</span></summary>
                  <div className="attachment-fields">
                    <label><span>Titre</span><input disabled={mobileReadOnly} maxLength={255} value={draft.title ?? ''} onChange={(event) => setField(attachment, 'title', event.target.value)} placeholder="Nom plus facile à retrouver" /></label>
                    <label><span>Type de document</span><input disabled={mobileReadOnly} maxLength={255} value={draft.category ?? ''} onChange={(event) => setField(attachment, 'category', event.target.value)} placeholder="Récépissé, courrier, visa…" /></label>
                    <label><span>Date du document</span><input disabled={mobileReadOnly} type="date" value={draft.document_date ?? ''} onInput={(event) => setField(attachment, 'document_date', event.currentTarget.value)} /></label>
                    <label><span>Reçu ou envoyé le</span><input disabled={mobileReadOnly} type="date" value={draft.received_date ?? ''} onInput={(event) => setField(attachment, 'received_date', event.currentTarget.value)} /></label>
                    <label className="wide"><span>Émetteur</span><input disabled={mobileReadOnly} maxLength={255} value={draft.issuer ?? ''} onChange={(event) => setField(attachment, 'issuer', event.target.value)} placeholder="Administration, consultant, équipe…" /></label>
                    <label className="wide"><span>Note libre</span><textarea disabled={mobileReadOnly} maxLength={4000} rows={3} value={draft.note ?? ''} onChange={(event) => setField(attachment, 'note', event.target.value)} placeholder="Précision facultative" /></label>
                  </div>
                  <div className="attachment-actions">
                    {!replacedIds.has(attachment.id) ? <label className={`secondary-button compact${mobileReadOnly ? ' is-disabled' : ''}`}>
                      {workingId === attachment.id ? <LoaderCircle className="spin" size={16} /> : <RefreshCw size={16} />} Nouvelle version
                      <input disabled={mobileReadOnly || busy || workingId !== null} type="file" onChange={(event) => { const file = event.target.files?.[0]; if (file) void upload(file, attachment); event.currentTarget.value = '' }} />
                    </label> : <span className="attachment-version-note">Version remplacée et conservée</span>}
                    <button className="primary-button compact" disabled={mobileReadOnly || busy || workingId !== null} onClick={() => void saveMetadata(attachment)} type="button"><Save size={16} /> Enregistrer les précisions</button>
                  </div>
                </details>
              </article>
            )
          })}
        </div>
      </aside>
    </div>
  )
}
